import {FIELD_BIOME_TEXTURES} from '../../../configs/fieldBiomeEffects';
import {SPORE_PUFFS_KIND} from '../../../configs/biomeEffects/sporePuffs';
import {resolveSpawnBox} from './biomeContext';
import {
  TWO_PI,
  clamp01,
  expandBox,
  lerp,
  pick,
  pickPointInBox,
  pickWeighted,
  randomIn,
  randomInt,
  smoothstep,
  toRadians,
  writeHiddenPose,
  writePose,
} from './biomeMath';

/**
 * ОБЛАЧКА СПОР (грибная подложка). Чистые функции без Phaser и DOM: по этому
 * описанию частицы каждый кадр ставит сцена (scenes/FieldBiomeEffects).
 *
 * Дорожек две, и у обеих группа — один выдох: вспышка на источнике и рой
 * пылинок его цвета. Пылинки вылетают из источника по лучам во все стороны и
 * замирают облачком; облачко плывет, расползаясь, каждая пылинка покачивается
 * и мерцает, а в последние секунды пылинки тают одна за другой.
 *
 * 1. КОЛОНИИ НА ПЕЙЗАЖЕ. Вспышка — мягкое пятно на самой колонии, на земле.
 *    Облачко плывет по ветру матча (тому же, что несет облака), виляя поперек
 *    него.
 *
 *    КУРС. Колонии стоят кольцом вокруг поля, а ветер матча дует вдоль одной
 *    оси: строго по ветру через поле прошли бы споры двух-трех колоний,
 *    остальные уплыли бы мимо. Поэтому облачко плывет по ветру, но забирает в
 *    сторону поля — к точке там, куда смотрит камера, — не больше чем на
 *    заданный угол. Чаще выдыхают колонии, от которых до поля по такому курсу
 *    можно доплыть, и те, чье облачко пройдет через кадр.
 *
 * 2. ГРИБЫ-ГОРЫ НА ПОЛЕ (жетон горы на этой подложке нарисован грибом).
 *    Вспышка — на шляпке, поверх жетона. Облачко меньше и живет недолго:
 *    поднимается над шляпкой, расходится, отплывает строго по ветру на
 *    ячейку-другую и тает. Выдыхает гриб, чье облачко уже растаяло, охотнее —
 *    тот, что в кадре, и не тот, что выдыхал перед этим.
 *
 *    Горы поля читаются при рождении каждой группы (context.mountains): их
 *    ставят и убирают, пока эффект жив. Нет гор — дорожка грибов молчит.
 */

const GLOW = 'glow';
const MOTE = 'mote';

// Во сколько раз покачивание пылинки по вертикали медленнее, чем по
// горизонтали: пылинка ходит не по кругу, а по петле, которая не повторяется
const BOB_PERIOD_RATIO = 1.3;
// По стольким точкам пути облачка проверяется, пройдет ли оно через кадр
const PATH_PROBES = 8;

/** Быстрый вылет с плавной остановкой */
const easeOutCubic = (share) => 1 - (1 - share) ** 3;

/** Угол, приведенный к отрезку от -180 до 180 градусов (в радианах) */
const wrapAngle = (angle) => Math.atan2(Math.sin(angle), Math.cos(angle));

const checkIfInBox = (box, x, y) => x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;

/** Гора — это ее ячейка: по середине ячейки облачко помнит свой гриб */
const resolveMountainKey = ({x, y}) => `${x}:${y}`;

/** Вспышка источника: разгорается за riseMs и гаснет за fallMs; 0 — ее не видно */
const resolveFlashPulse = ({riseMs, fallMs}, elapsedMs) => Math.min(
  smoothstep(0, riseMs, elapsedMs), 1 - smoothstep(riseMs, riseMs + fallMs, elapsedMs)
);

/** Сколько облачко проплыло своим курсом за ageMs: плавный разгон за rampMs, дальше — ровно */
const resolveTravel = (speed, rampMs, ageMs) => (
  (speed * (ageMs < rampMs ? (ageMs * ageMs) / (2 * rampMs) : ageMs - rampMs / 2)) / 1000
);

const createTracks = (context, params) => {
  const {hexSize, wind, coverage, fieldBox, cells} = context;
  const {glow, motes, burst, drift, emission, mountains} = params;
  const windAngle = Math.atan2(wind.y, wind.x);
  const maxLean = toRadians(drift.leanDeg);
  const fieldCentre = {x: (fieldBox.left + fieldBox.right) / 2, y: (fieldBox.top + fieldBox.bottom) / 2};
  const fieldCorners = [
    [fieldBox.left, fieldBox.top], [fieldBox.right, fieldBox.top],
    [fieldBox.left, fieldBox.bottom], [fieldBox.right, fieldBox.bottom],
  ];
  const weave = hexSize * drift.weaveFactor;
  // Наибольший размах облачка: с ним оно задевает кадр, даже пролетая рядом
  const cloudReach = hexSize * burst.radiusFactor.max * drift.growth;
  const meanSpeed = (hexSize * (drift.speedFactor.min + drift.speedFactor.max)) / 2;
  const meanReachShare = (drift.reachShare.min + drift.reachShare.max) / 2;

  // ------------------------------------------------------------------ пылинки

  /**
   * Пылинки одного облачка — общие у колонии и у гриба-горы.
   *
   * @param {Function} rng
   * @param {Object} look — числа облачка: {motes, burst}
   * @param {number} tint — краска источника
   * @param {number} flightMs — сколько живет облачко
   */
  const buildMotes = (rng, look, tint, flightMs) => {
    const particles = [];
    const count = randomInt(rng, look.motes.count.min, look.motes.count.max);
    for (let index = 0; index < count; index += 1) {
      // Лучи — по кругу, каждый в своей доле: выброс идет во все стороны, а не комком
      const angle = ((index + rng()) / count) * TWO_PI;
      particles.push({
        kind: MOTE,
        layer: look.motes.layer,
        texture: FIELD_BIOME_TEXTURES.DOT,
        tint,
        rayX: Math.cos(angle),
        rayY: Math.sin(angle),
        // Корень — чтобы пылинки легли по площади круга ровно, а не сгрудились у источника
        radius: hexSize * lerp(look.burst.radiusFactor.min, look.burst.radiusFactor.max, Math.sqrt(rng())),
        size: hexSize * randomIn(rng, look.motes.sizeFactor),
        // Тает в свой миг последних секунд облачка
        dieMs: flightMs - rng() * Math.min(look.motes.staggerMs, flightMs / 2),
        twinklePeriodMs: randomIn(rng, look.motes.twinklePeriodMs),
        twinklePhase: rng() * TWO_PI,
        bob: hexSize * randomIn(rng, look.motes.bobFactor),
        bobPeriodMs: randomIn(rng, look.motes.bobPeriodMs),
        bobPhase: rng() * TWO_PI,
      });
    }
    return particles;
  };

  /**
   * Поза пылинки в облачке, середина которого сейчас в точке (centreX, centreY).
   *
   * @param {Object} out
   * @param {Object} look — числа облачка: {motes, burst, growth}
   * @param {Object} group
   * @param {Object} particle
   * @param {number} ageMs — время с вылета спор
   */
  const writeMotePose = (out, look, group, particle, ageMs, centreX, centreY) => {
    const lifeShare = ageMs / group.flightMs;
    // Вылет по своему лучу, потом облачко расползается
    const spread = particle.radius * easeOutCubic(clamp01(ageMs / look.burst.durationMs))
      * lerp(1, look.growth, lifeShare);
    // Покачивание начинается, когда выброс улегся: сам выброс — ровно по лучам
    const bob = particle.bob * smoothstep(0, look.burst.durationMs, ageMs);
    const x = centreX + particle.rayX * spread
      + bob * Math.sin((TWO_PI * ageMs) / particle.bobPeriodMs + particle.bobPhase);
    const y = centreY + particle.rayY * spread
      + bob * Math.cos((TWO_PI * ageMs) / (particle.bobPeriodMs * BOB_PERIOD_RATIO) + particle.bobPhase);

    const fade = Math.min(
      smoothstep(0, look.motes.fadeInMs, ageMs),
      1 - smoothstep(particle.dieMs - look.motes.fadeOutMs, particle.dieMs, ageMs)
    );
    const twinkle = 1 - look.motes.twinkleDepth
      * (0.5 + 0.5 * Math.sin((TWO_PI * ageMs) / particle.twinklePeriodMs + particle.twinklePhase));
    // Над игровыми ячейками — тусклее
    const overField = lerp(1, look.motes.overFieldAlphaShare, smoothstep(0.3, 0.6, coverage.sample(x, y)));
    const size = particle.size * lerp(1, look.motes.riseScale, lifeShare);
    return writePose(out, x, y, look.motes.alpha * fade * twinkle * overField, size, size, 0);
  };

  // ------------------------------------------------------------------ колонии

  const colonyLook = {motes, burst, growth: drift.growth};

  /** На сколько курс от колонии к точке отклоняется от ветра (со знаком) */
  const resolveTurn = (colony, target) => wrapAngle(Math.atan2(target.y - colony.y, target.x - colony.x) - windAngle);

  /** Курс облачка: по ветру, забирая к цели не больше чем на maxLean */
  const resolveHeading = (colony, target) => (
    windAngle + Math.max(-maxLean, Math.min(maxLean, resolveTurn(colony, target)))
  );

  /**
   * Путь облачка: мимо цели и дальше, на долю reachShare оставшегося пути до
   * дальнего края поля. Срок жизни — в пределах lifeMs: чем дальше колония от
   * поля, тем дольше живет облачко, но не дольше потолка (на очень большом
   * поле оно тает, не доплыв до середины)
   */
  const resolveFlight = (colony, target, speed, reachShare) => {
    const heading = resolveHeading(colony, target);
    const headingX = Math.cos(heading);
    const headingY = Math.sin(heading);
    const toTarget = Math.max(0, (target.x - colony.x) * headingX + (target.y - colony.y) * headingY);
    const toFarEdge = Math.max(
      toTarget, ...fieldCorners.map(([x, y]) => (x - colony.x) * headingX + (y - colony.y) * headingY)
    );
    const flightMs = Math.min(
      drift.lifeMs.max, Math.max(drift.lifeMs.min, (lerp(toTarget, toFarEdge, reachShare) / speed) * 1000)
    );
    return {headingX, headingY, flightMs};
  };

  const colonies = params.colonies.map((colony, index) => {
    const oval = context.toWorldOval(colony);
    const placed = {index, x: oval.x, y: oval.y, radius: oval.radiusX, tint: params.tints[colony.tint]};
    // Можно ли от колонии доплыть до поля: 1 — курс на середину поля укладывается
    // в допустимый наклон, меньше — поле остается в стороне или позади
    const missTurn = Math.max(0, Math.abs(resolveTurn(placed, fieldCentre)) - maxLean);
    placed.weight = (colony.weight ?? 1) * Math.max(emission.minWindWeight, Math.cos(missTurn));
    return placed;
  });

  /**
   * Цель облачка — игровая ячейка там, куда смотрит камера. Именно ячейка, а
   * не точка рамки поля: поле не прямоугольное, и по углам его рамки — пейзаж
   */
  const pickTarget = (rng, view) => {
    const box = resolveSpawnBox(context, view, {fieldMarginFactor: 0, viewMarginFactor: drift.targetViewMarginFactor});
    if (cells.length === 0) {
      return pickPointInBox(rng, box);
    }
    const cellsInBox = cells.filter((cell) => checkIfInBox(box, cell.x, cell.y));
    return pick(rng, cellsInBox.length > 0 ? cellsInBox : cells);
  };

  /** Пройдет ли облачко колонии через то, что видит камера */
  const checkIfPassesView = (colony, view) => {
    const viewCentre = {x: (view.left + view.right) / 2, y: (view.top + view.bottom) / 2};
    const {headingX, headingY, flightMs} = resolveFlight(colony, viewCentre, meanSpeed, meanReachShare);
    const travel = (meanSpeed * flightMs) / 1000;
    const box = expandBox(view, cloudReach);
    for (let probe = 0; probe < PATH_PROBES; probe += 1) {
      const along = (travel * probe) / (PATH_PROBES - 1);
      if (checkIfInBox(box, colony.x + headingX * along, colony.y + headingY * along)) {
        return true;
      }
    }
    return false;
  };

  const buildColonyGroup = ({rng, view, liveGroups}) => {
    // Пока облачко колонии в пути, она молчит: выдохи идут по кругу, а не из
    // одной и той же точки
    const rested = colonies.filter((colony) => !liveGroups.some((group) => group.colonyIndex === colony.index));
    const colony = pickWeighted(rng, rested, (candidate) => (
      candidate.weight * (view && checkIfPassesView(candidate, view) ? emission.inViewBoost : 1)
    ));
    if (!colony) {
      return null;
    }

    const target = pickTarget(rng, view);
    const speed = hexSize * randomIn(rng, drift.speedFactor);
    const {headingX, headingY, flightMs} = resolveFlight(colony, target, speed, randomIn(rng, drift.reachShare));
    const flash = {
      kind: GLOW,
      layer: glow.layer,
      texture: FIELD_BIOME_TEXTURES.DOT,
      tint: colony.tint,
      size: colony.radius * 2 * glow.scale,
    };

    return {
      durationMs: glow.leadMs + flightMs,
      colonyIndex: colony.index,
      x: colony.x,
      y: colony.y,
      headingX,
      headingY,
      speed,
      flightMs,
      weavePhase: rng() * TWO_PI,
      particles: [flash, ...buildMotes(rng, colonyLook, colony.tint, flightMs)],
    };
  };

  const resolveColonyPose = (group, particle, elapsedMs, out) => {
    if (particle.kind === GLOW) {
      // Колония разгорается и медленно гаснет
      const pulse = resolveFlashPulse(glow, elapsedMs);
      return pulse > 0
        ? writePose(out, group.x, group.y, glow.alpha * pulse, particle.size, particle.size, 0)
        : writeHiddenPose(out);
    }

    // Споры вылетают, когда колония уже разгорелась
    const ageMs = elapsedMs - glow.leadMs;
    if (!(ageMs > 0) || !(ageMs < particle.dieMs)) {
      return writeHiddenPose(out);
    }
    // Облачко: плавный разгон, дальше — ровно своим курсом, виляя поперек него
    const along = resolveTravel(group.speed, drift.rampMs, ageMs);
    const aside = weave * smoothstep(0, drift.rampMs, ageMs)
      * (Math.sin((TWO_PI * ageMs) / drift.weavePeriodMs + group.weavePhase) - Math.sin(group.weavePhase));
    return writeMotePose(
      out, colonyLook, group, particle, ageMs,
      group.x + group.headingX * along - group.headingY * aside,
      group.y + group.headingY * along + group.headingX * aside
    );
  };

  // --------------------------------------------------------------- грибы-горы

  const mountainLook = {motes: mountains.motes, burst: mountains.burst, growth: mountains.drift.growth};
  const liftHeight = hexSize * mountains.rise.heightFactor;

  // Гриб, выдохнувший последним: дважды подряд один и тот же не выдыхает.
  // Дорожки создаются заново на каждое поле и каждую смену ветра — память не
  // переживает ни того, ни другого, и это не страшно
  let lastMountainKey = null;

  const buildMountainGroup = ({rng, view, liveGroups}) => {
    // Пока облачко гриба в воздухе, он молчит. Горы — нынешние: список один на
    // все время жизни эффекта, а его содержимое меняется (редактор поля)
    const quiet = context.mountains.filter((mountain) => (
      !liveGroups.some((group) => group.mountainKey === resolveMountainKey(mountain))
    ));
    const rested = quiet.filter((mountain) => resolveMountainKey(mountain) !== lastMountainKey);
    // Гриб на поле один — отдыхать ему не с кем
    const mountain = pickWeighted(rng, rested.length > 0 ? rested : quiet, (candidate) => (
      view && checkIfInBox(view, candidate.x, candidate.y) ? mountains.inViewBoost : 1
    ));
    if (!mountain) {
      return null;
    }
    lastMountainKey = resolveMountainKey(mountain);

    const tint = params.tints[pick(rng, mountains.tints)];
    const flightMs = randomIn(rng, mountains.drift.lifeMs);
    // Скорость — под путь: за свою жизнь облачко проплывает ровно reach
    const reach = hexSize * randomIn(rng, mountains.drift.reachFactor);
    const flash = {
      kind: GLOW,
      layer: mountains.flash.layer,
      texture: FIELD_BIOME_TEXTURES.DOT,
      tint,
      size: hexSize * mountains.flash.sizeFactor,
    };

    return {
      durationMs: mountains.flash.leadMs + flightMs,
      mountainKey: lastMountainKey,
      // Источник — шляпка гриба на жетоне, а не середина ячейки
      x: mountain.x + hexSize * mountains.cap.dx,
      y: mountain.y + hexSize * mountains.cap.dy,
      headingX: wind.x,
      headingY: wind.y,
      speed: (reach * 1000) / (flightMs - mountains.drift.rampMs / 2),
      flightMs,
      particles: [flash, ...buildMotes(rng, mountainLook, tint, flightMs)],
    };
  };

  const resolveMountainPose = (group, particle, elapsedMs, out) => {
    if (particle.kind === GLOW) {
      // Шляпка разгорается и гаснет
      const pulse = resolveFlashPulse(mountains.flash, elapsedMs);
      return pulse > 0
        ? writePose(out, group.x, group.y, mountains.flash.alpha * pulse, particle.size, particle.size, 0)
        : writeHiddenPose(out);
    }

    // Споры вылетают, когда шляпка уже разгорелась
    const ageMs = elapsedMs - mountains.flash.leadMs;
    if (!(ageMs > 0) || !(ageMs < particle.dieMs)) {
      return writeHiddenPose(out);
    }
    // Облачко поднимается над шляпкой и, разогнавшись, уходит по ветру
    const along = resolveTravel(group.speed, mountains.drift.rampMs, ageMs);
    const lift = liftHeight * easeOutCubic(clamp01(ageMs / mountains.rise.durationMs));
    return writeMotePose(
      out, mountainLook, group, particle, ageMs,
      group.x + group.headingX * along,
      group.y + group.headingY * along - lift
    );
  };

  return [{
    schedule: {
      firstDelayMs: params.firstDelayMs,
      intervalMs: params.intervalMs,
      maxGroups: params.maxGroups,
      prewarm: params.prewarm,
    },
    buildGroup: buildColonyGroup,
    resolvePose: resolveColonyPose,
  }, {
    schedule: {
      firstDelayMs: mountains.firstDelayMs,
      intervalMs: mountains.intervalMs,
      maxGroups: mountains.maxGroups,
    },
    buildGroup: buildMountainGroup,
    resolvePose: resolveMountainPose,
  }];
};

const sporePuffs = {
  kind: SPORE_PUFFS_KIND,
  textures: [FIELD_BIOME_TEXTURES.DOT],
  createTracks,
};

export default sporePuffs;
